import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Tooltip,
  Avatar,
  AvatarGroup,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import { useTranslation } from "react-i18next";
import { getInitials } from "../../../utils/utils";
import { formatDeletedUserName } from "../utils/teamBoardCardFilters";
import TaskStageTimeBar from "./TaskStageTimeBar";
import { formatTaskDisplayName } from "../utils/taskDisplayName";
import { attachmentKind } from "../utils/taskAttachmentKind";

function checklistSummary(checklists) {
  let done = 0;
  let total = 0;
  for (const cl of checklists || []) {
    for (const item of cl.items || []) {
      total += 1;
      if (item.isDone) done += 1;
    }
  }
  return { done, total };
}

function isOverdue(dueAt) {
  if (!dueAt) return false;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return false;
  return d < new Date();
}

function isDueSoon(dueAt) {
  if (!dueAt || isOverdue(dueAt)) return false;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return false;
  const diff = d.getTime() - Date.now();
  return diff >= 0 && diff <= 24 * 60 * 60 * 1000;
}

function pickCoverAttachment(attachments = []) {
  return attachments.find((a) => attachmentKind(a) === "image") || null;
}

function MetaChip({ icon, label, tooltip, color = "default", sx = {} }) {
  const chip = (
    <Chip
      size="small"
      icon={icon || undefined}
      label={label}
      sx={{
        height: 22,
        maxWidth: "100%",
        borderRadius: 1.25,
        fontWeight: 600,
        fontSize: "0.68rem",
        "& .MuiChip-icon": { ml: 0.5, mr: -0.25, fontSize: 14 },
        "& .MuiChip-label": { px: label ? 0.75 : 0.5 },
        ...sx,
      }}
      color={color === "default" ? undefined : color}
      variant={color === "default" ? "filled" : "filled"}
    />
  );
  return tooltip ? (
    <Tooltip title={tooltip} enterDelay={400}>
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
        {chip}
      </Box>
    </Tooltip>
  ) : (
    chip
  );
}

const TaskCard = ({ card, onOpen, dragHandleProps, inDoneColumn = false }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();

  const descriptionPreview = useMemo(() => {
    const raw = String(card.description || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!raw) return null;
    return raw.length > 90 ? `${raw.slice(0, 90)}…` : raw;
  }, [card.description]);

  const completedInColumn = inDoneColumn && Boolean(card.dueAt);
  const overdue = !inDoneColumn && isOverdue(card.dueAt);
  const dueSoon = !inDoneColumn && isDueSoon(card.dueAt);
  const assignees = card.assigneeUsers || card.assignees || [];
  const labels = card.labels || [];
  const { done: checklistDone, total: checklistTotal } = checklistSummary(card.checklists);
  const checklistComplete = checklistTotal > 0 && checklistDone === checklistTotal;
  const attachmentCount = (card.attachments || []).length;
  const commentCount = (card.comments || []).length;
  const cover = useMemo(() => pickCoverAttachment(card.attachments), [card.attachments]);
  const childCount = Array.isArray(card.children) ? card.children.length : 0;
  const hasParent = Boolean(card.parentCardId || card.parent);
  const pendingLinkedReview = Boolean(card.linkedCompletionPendingReview);
  const hasLinks = hasParent || childCount > 0;
  const clientLabel = card.clientName ? String(card.clientName).trim() : "";

  const dueLabel = card.dueAt
    ? new Date(card.dueAt).toLocaleDateString("pt-BR", { day: "numeric", month: "short" })
    : null;
  const durationLabel = card.estimatedDuration
    ? String(card.estimatedDuration).slice(0, 5)
    : null;

  const accentColor = inDoneColumn
    ? theme.palette.success.main
    : overdue
      ? theme.palette.error.main
      : pendingLinkedReview
        ? theme.palette.warning.main
        : card.isRequired
          ? theme.palette.warning.main
          : hasLinks
            ? theme.palette.info.main
            : theme.palette.primary.main;

  const hasStatusBadges = card.isRequired || hasLinks || pendingLinkedReview || Boolean(clientLabel);
  const hasFooterMeta =
    dueLabel ||
    durationLabel ||
    descriptionPreview ||
    checklistTotal > 0 ||
    attachmentCount > 0 ||
    commentCount > 0 ||
    assignees.length > 0 ||
    card.isRequired ||
    hasLinks ||
    pendingLinkedReview;

  const handleOpen = (e) => {
    if (e.defaultPrevented) return;
    onOpen(card);
  };

  const avatarSize = inDoneColumn ? 22 : 24;
  const quietBg = alpha(theme.palette.action.active, theme.palette.mode === "dark" ? 0.12 : 0.06);

  return (
    <Card
      elevation={0}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        position: "relative",
        bgcolor: inDoneColumn
          ? (th) =>
              th.palette.mode === "dark"
                ? alpha(th.palette.background.paper, 0.72)
                : alpha(th.palette.grey[50], 0.98)
          : "background.paper",
        boxShadow: (th) =>
          inDoneColumn
            ? "none"
            : th.palette.mode === "dark"
              ? `inset 0 0 0 1px ${alpha(th.palette.divider, 0.5)}`
              : "0 1px 2px rgba(15,23,42,0.05), 0 1px 1px rgba(15,23,42,0.03)",
        border: "1px solid",
        borderColor: (th) => alpha(th.palette.divider, inDoneColumn ? 0.35 : 0.55),
        borderLeft: `3px solid ${alpha(accentColor, inDoneColumn ? 0.45 : overdue || card.isRequired ? 0.9 : 0.55)}`,
        overflow: "hidden",
        opacity: inDoneColumn ? 0.9 : 1,
        transition:
          "box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease, opacity 0.18s ease",
        "&:hover": {
          opacity: 1,
          transform: inDoneColumn ? "none" : "translateY(-1px)",
          borderColor: (th) => alpha(accentColor, 0.55),
          boxShadow: (th) =>
            inDoneColumn
              ? `0 1px 4px ${alpha(th.palette.common.black, 0.06)}`
              : th.palette.mode === "dark"
                ? `0 0 0 1px ${alpha(accentColor, 0.4)}`
                : `0 8px 18px rgba(15,23,42,0.1), 0 0 0 1px ${alpha(accentColor, 0.28)}`,
        },
        "&:active": { transform: inDoneColumn ? "none" : "translateY(0) scale(0.995)" },
      }}
      onClick={handleOpen}
    >
      {cover ? (
        <Box sx={{ position: "relative", lineHeight: 0 }}>
          <Box
            component="img"
            src={cover.fileUrl}
            alt=""
            sx={{
              width: "100%",
              height: 112,
              objectFit: "cover",
              display: "block",
              bgcolor: "action.hover",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: (th) =>
                `linear-gradient(to top, ${alpha(th.palette.common.black, 0.22)} 0%, transparent 50%)`,
              pointerEvents: "none",
            }}
          />
        </Box>
      ) : null}

      <CardContent
        sx={{
          p: 1.25,
          pt: cover ? 1 : 1.25,
          "&:last-child": { pb: 1.25 },
        }}
        {...dragHandleProps}
      >
        {labels.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.85 }}>
            {labels.map((l) => (
              <Tooltip key={l.id} title={l.name}>
                <Box
                  sx={{
                    height: 7,
                    minWidth: 28,
                    maxWidth: 72,
                    flex: labels.length === 1 ? "1 1 auto" : undefined,
                    borderRadius: 999,
                    bgcolor: l.color || "primary.main",
                    boxShadow: (th) => `inset 0 0 0 1px ${alpha(th.palette.common.black, 0.1)}`,
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        )}

        {hasStatusBadges ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
            {clientLabel ? (
              <Tooltip title={t("cardClientLabel")}>
                <Chip
                  size="small"
                  icon={<BusinessOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  label={clientLabel}
                  sx={{
                    height: 22,
                    maxWidth: "100%",
                    borderRadius: 1.25,
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    bgcolor: quietBg,
                    color: "text.secondary",
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            ) : null}
            {card.isRequired ? (
              <Chip
                size="small"
                icon={<FlagOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={t("cardRequiredShort")}
                sx={{
                  height: 22,
                  borderRadius: 1.25,
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  bgcolor: alpha(theme.palette.warning.main, 0.14),
                  color: theme.palette.mode === "dark" ? "warning.light" : "warning.dark",
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                  "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
                }}
              />
            ) : null}
            {hasParent ? (
              <Tooltip title={formatTaskDisplayName(card.parent?.id, card.parent?.title) || t("linkedTaskParent")}>
                <Chip
                  size="small"
                  icon={<LinkOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  label={t("linkedTaskParent")}
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.mode === "dark" ? "info.light" : "info.dark",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                    "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
                  }}
                />
              </Tooltip>
            ) : null}
            {pendingLinkedReview ? (
              <Tooltip title={t("linkedCompletionPendingReviewHint")}>
                <Chip
                  size="small"
                  icon={<RuleOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  label={t("linkedCompletionPendingReview")}
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    bgcolor: alpha(theme.palette.warning.main, 0.16),
                    color: theme.palette.mode === "dark" ? "warning.light" : "warning.dark",
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                    "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
                  }}
                />
              </Tooltip>
            ) : childCount > 0 ? (
              <Tooltip title={t("linkedTaskChildren")}>
                <Chip
                  size="small"
                  icon={<AccountTreeOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  label={
                    card.linkedCompletedCount != null
                      ? t("cardLinkedChildrenProgress", {
                          done: card.linkedCompletedCount,
                          total: childCount,
                        })
                      : childCount
                  }
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    bgcolor: quietBg,
                    color: "text.secondary",
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
                  }}
                />
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}

        <Typography
          variant="body2"
          component="p"
          sx={{
            fontWeight: inDoneColumn ? 500 : 600,
            fontSize: "0.8375rem",
            wordBreak: "break-word",
            lineHeight: 1.4,
            letterSpacing: "-0.01em",
            color: inDoneColumn ? "text.secondary" : "text.primary",
            textDecoration: inDoneColumn ? "line-through" : "none",
            textDecorationColor: alpha(theme.palette.text.secondary, 0.45),
            display: "-webkit-box",
            WebkitLineClamp: descriptionPreview ? 2 : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            m: 0,
          }}
        >
          {formatTaskDisplayName(card.id, card.title)}
        </Typography>

        {descriptionPreview && !inDoneColumn ? (
          <Typography
            variant="caption"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mt: 0.55,
              lineHeight: 1.4,
              color: "text.secondary",
              opacity: 0.92,
            }}
          >
            {descriptionPreview}
          </Typography>
        ) : null}

        {hasFooterMeta ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.75}
            sx={{
              mt: 1.1,
              pt: 0.9,
              gap: 0.75,
              borderTop: `1px solid ${alpha(theme.palette.divider, inDoneColumn ? 0.28 : 0.42)}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              spacing={0.5}
              sx={{ minWidth: 0, flex: 1 }}
            >
              {dueLabel ? (
                <MetaChip
                  icon={
                    completedInColumn ? (
                      <CheckCircleOutlineIcon />
                    ) : (
                      <ScheduleOutlinedIcon />
                    )
                  }
                  label={dueLabel}
                  tooltip={
                    completedInColumn
                      ? t("cardDueCompleted")
                      : overdue
                        ? t("cardDueOverdue")
                        : dueSoon
                          ? t("cardDueSoon")
                          : t("cardDueDate")
                  }
                  sx={
                    completedInColumn
                      ? {
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: "success.main",
                        }
                      : overdue
                        ? {
                            bgcolor: "error.main",
                            color: "error.contrastText",
                            "& .MuiChip-icon": { color: "inherit" },
                          }
                        : dueSoon
                          ? {
                              bgcolor: alpha(theme.palette.warning.main, 0.18),
                              color:
                                theme.palette.mode === "dark" ? "warning.light" : "warning.dark",
                            }
                          : {
                              bgcolor: quietBg,
                              color: "text.secondary",
                            }
                  }
                />
              ) : null}

              {durationLabel ? (
                <MetaChip
                  icon={<AccessTimeOutlinedIcon />}
                  label={durationLabel}
                  tooltip={t("cardEstimatedDuration")}
                  sx={{ bgcolor: quietBg, color: "text.secondary" }}
                />
              ) : null}

              {!descriptionPreview && card.description ? (
                <Tooltip title={t("cardHasDescription")}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: 1.25,
                      bgcolor: quietBg,
                      color: "text.secondary",
                    }}
                  >
                    <SubjectOutlinedIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Tooltip>
              ) : null}

              {commentCount > 0 ? (
                <MetaChip
                  icon={<ChatBubbleOutlineIcon />}
                  label={commentCount}
                  tooltip={t("commentCount", { count: commentCount })}
                  sx={{ bgcolor: quietBg, color: "text.secondary" }}
                />
              ) : null}

              {attachmentCount > 0 ? (
                <MetaChip
                  icon={<AttachFileOutlinedIcon />}
                  label={attachmentCount}
                  tooltip={t("attachmentCount", { count: attachmentCount })}
                  sx={{ bgcolor: quietBg, color: "text.secondary" }}
                />
              ) : null}

              {checklistTotal > 0 ? (
                <MetaChip
                  icon={<CheckBoxOutlinedIcon />}
                  label={`${checklistDone}/${checklistTotal}`}
                  tooltip={t("checklistProgress", { done: checklistDone, total: checklistTotal })}
                  sx={
                    checklistComplete
                      ? {
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: "success.main",
                          "& .MuiChip-icon": { color: "inherit" },
                        }
                      : { bgcolor: quietBg, color: "text.secondary" }
                  }
                />
              ) : null}
            </Stack>

            {assignees.length > 0 ? (
              <AvatarGroup
                max={3}
                sx={{
                  flexShrink: 0,
                  opacity: inDoneColumn ? 0.85 : 1,
                  "& .MuiAvatar-root": {
                    width: avatarSize,
                    height: avatarSize,
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    border: "2px solid",
                    borderColor: "background.paper",
                    ml: assignees.length > 1 ? -0.55 : 0,
                  },
                }}
              >
                {assignees.map((a, idx) => {
                  const id = typeof a === "object" ? a.id : a;
                  const name = formatDeletedUserName(
                    typeof a === "object" ? a.nome || a.name || String(id) : String(a),
                    Boolean(a?.deleted),
                    t("deletedUserMark")
                  );
                  const avatarSrc = typeof a === "object" ? a.imagem || a.avatarUrl : null;
                  return (
                    <Tooltip key={id ?? idx} title={name}>
                      <Avatar
                        src={avatarSrc || undefined}
                        sx={{
                          bgcolor: inDoneColumn ? "action.selected" : "primary.main",
                          color: inDoneColumn ? "text.secondary" : "primary.contrastText",
                        }}
                      >
                        {getInitials(name)}
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
            ) : null}
          </Stack>
        ) : null}
      </CardContent>
      {(card.stageTime || []).length > 0 ? (
        <TaskStageTimeBar
          stageTime={card.stageTime}
          totalSec={card.stageTimeTotalSec}
          height={3}
        />
      ) : null}
    </Card>
  );
};

export default TaskCard;
